import { Spinner } from 'design-system';
import { Church } from 'lucide-react';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { getFindGravePath } from '../../../../../service/constants';
import { cachedJsonFetch } from '../../../../../service/cache';
import { fetch_request } from '../../../../../service/request';
import Content from '../Component/Content';
import Header from '../Component/Header';
import Paginator from '../Component/Paginator';
import styles from './Index.module.css';

const Index = () => {
  const intl = useIntl();
  const [searchInputValue, setSearchInputValue] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [municipalitiesCount, setMunicipalitiesCount] = useState(0);
  const [municipalitiesList, setMunicipalitiesList] = useState([]);
  const [countryId, setCountryId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const loadCountries = async () => {
      try {
        const { data } = await cachedJsonFetch('/api/countries', {
          fetcher: fetch_request,
          cacheKey: 'countries:all',
          ttlMs: 60 * 60 * 1000,
        });
        if (cancelled) return;

        const countries = data?.countries || [];
        const host = window.location.hostname;

        const hostMatchers = [
          {
            test: (h) => h.includes('natgrav.se'),
            pattern: /(sweden|sverige|ruotsi)/i,
          },
          {
            test: (h) => h.includes('netgrav.dk'),
            pattern: /(denmark|danmark|dania)/i,
          },
          {
            test: (h) => h.includes('verkkohauta.fi'),
            pattern: /(finland|suomi)/i,
          },
          {
            test: (h) => h.includes('nettgrav.com'),
            pattern: /(united states|usa)/i,
          },
        ];

        const matcher = hostMatchers.find((m) => m.test(host));
        const pattern = matcher?.pattern || /(norway|norge|norja)/i;
        const country = countries.find((c) => pattern.test(c.name));
        if (country) setCountryId(country.id);
      } catch (err) {
        console.log(err);
      }
    };

    loadCountries();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (countryId === null) return;
    let cancelled = false;
    const url = `/api/municipalities_count?country_id=${countryId}`;

    const loadCounts = async () => {
      try {
        const { data } = await cachedJsonFetch(url, {
          fetcher: fetch_request,
          cacheKey: `municipality-count:${countryId}`,
          ttlMs: 10 * 60 * 1000,
        });
        if (cancelled) return;
        setMunicipalitiesCount(data?.count || 0);
      } catch (err) {
        console.log(err);
      }
    };

    loadCounts();
    return () => {
      cancelled = true;
    };
  }, [countryId]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchInputValue]);

  useEffect(() => {
    let cancelled = false;
    if (countryId === null) return undefined;

    const fetchMunicipalities = async () => {
      const cacheKey = `municipalities:${countryId || 'all'}:${searchInputValue || 'all'}:${currentPage}`;
      setLoading(true);
      let url = `/api/search_municipalities?input=${searchInputValue}&page=${currentPage}`;
      if (countryId) {
        url += `&country_id=${countryId}`;
      }

      try {
        const { data } = await cachedJsonFetch(url, {
          fetcher: fetch_request,
          cacheKey,
          ttlMs: 5 * 60 * 1000,
        });
        if (cancelled) return;
        setMunicipalitiesList(data?.municipalities || []);
        setTotalPages(data?.total_pages || 0);
      } catch (err) {
        console.log(err);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchMunicipalities();

    return () => {
      cancelled = true;
    };
  }, [searchInputValue, currentPage, countryId]);

  return (
    <div className={styles.municipalityIndex}>
      <Header
        setSearchInputValue={setSearchInputValue}
        title={<FormattedMessage id="find_municipality" />}
        description={
          <FormattedMessage id="municipality_description" values={{ count: municipalitiesCount }} />
        }
        placeholder={intl.formatMessage({ id: 'search_municipality' })}
      />
      {!loading ? (
        <Content
          list={municipalitiesList}
          searchInputValue={searchInputValue}
          url={getFindGravePath()}
          logo={<Church size={130} style={{ color: 'var(--sky-4)' }} />}
          ctaMessageId="find_grave_municipality_cta"
        />
      ) : (
        <div className={styles.loadingSpinner}>
          <Spinner size={50} />
        </div>
      )}
      {municipalitiesCount > 8 && (
        <Paginator
          setCurrentPage={setCurrentPage}
          currentPage={currentPage}
          totalPages={totalPages}
        />
      )}
    </div>
  );
};

export default Index;
